import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { registrationApi } from "../../pages/services/leadService";
import { errorText, fmtDateTime } from "./leadUtils";
import "../../styles/Leads.css";

// Registrations from the website's "Register now", paid by UPI / bank transfer:
// each waits with its payment screenshot until an admin checks the payment in
// the bank / UPI app and approves it — that creates the student on SkillVerse
// Buddy (Registered, the fee paid, the chosen batch) and marks the lead
// Registered — or rejects it.
const TABS = [
  ["submitted", "Waiting for approval"],
  ["paid", "Approved"],
  ["rejected", "Rejected"],
  ["all", "All"],
];
const STATE = {
  submitted: ["Waiting", "scheduled"], processing: ["Approving…", "scheduled"], error: ["Approval failed", "failed"],
  paid: ["Approved", "sent"], rejected: ["Rejected", "skipped"], pending: ["Razorpay — not paid", "skipped"], cancelled: ["Razorpay — cancelled", "skipped"],
};
const rupees = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;
const isPdf = (s) => /\.pdf($|\?)/i.test(s?.url || "") || /\.pdf$/i.test(s?.fileName || "");

const Detail = ({ d }) => {
  const rows = [
    ["Date of birth", d.DOB ? new Date(d.DOB).toLocaleDateString("en-IN") : "—"],
    ["Alternative number", d.alternateContactNumber], ["Heard from", d.heardFrom],
    ["Address", [d.permanentAddress, d.permanentAddressCity, d.permanentAddressState].filter(Boolean).join(", ")],
    ["Current address", [d.alternateAddress, d.alternateAddressCity, d.alternateAddressState].filter(Boolean).join(", ")],
    ["Father", `${d.fatherName || ""} · ${d.fatherOccupation || ""} · ${d.fatherContactNumber || ""}`],
    ["Mother", `${d.motherName || ""} · ${d.motherOccupation || ""} · ${d.motherContactNumber || ""}`],
    ["Education", [d.educationLevel, d.schoolCollege, d.branch, d.schoolCollegePlace].filter(Boolean).join(" · ")],
    ["Year", d.currentlyPursuing ? `Studying — completes ${d.completionYear || "?"}` : `Completed ${d.completedIn || "?"}`],
    ["Score", d.score || "—"], ["Skills", d.skillsKnownAlready || "—"],
  ];
  return <dl className="ld-reg-detail">{rows.map(([k, v]) => <React.Fragment key={k}><dt>{k}</dt><dd>{v || "—"}</dd></React.Fragment>)}</dl>;
};

const LeadRegistrations = () => {
  const [tab, setTab] = useState("submitted");
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [notice, setNotice] = useState(null);       // { tone, text }

  const load = useCallback(() => {
    setLoading(true);
    registrationApi.list(tab)
      .then((r) => { setRows(r.data); setCounts(r.counts || {}); })
      .catch((e) => setNotice({ tone: "error", text: errorText(e) }))
      .finally(() => setLoading(false));
  }, [tab]);
  useEffect(() => { load(); }, [load]);

  const approve = async (r) => {
    const d = r.details || {};
    if (!window.confirm(`Approve ${d.studentName}'s registration?\n\nOnly if you have checked that ${rupees(r.amount)} reached the account${r.paymentRef ? ` (ref ${r.paymentRef})` : ""}.\n\nThis creates the student on SkillVerse Buddy (batch ${r.batchTitle}, Registered, ${rupees(r.amount)} paid) and marks the lead Registered.`)) return;
    setBusyId(r._id);
    try {
      const reg = await registrationApi.approve(r._id);
      setNotice({ tone: "success", text: `Approved: ${d.studentName} is ${reg.studentWasNew ? "now a student" : "added to the batch (already a student)"} on SkillVerse Buddy — they log in with ${d.contactNumber}.` });
    } catch (e) {
      setNotice({ tone: "error", text: errorText(e) });
    } finally {
      setBusyId(null);
      load();
    }
  };
  const reject = async (r) => {
    const reason = window.prompt(`Reject ${r.details?.studentName}'s registration? Why (shown on the lead's timeline):`, "Payment not received");
    if (reason === null) return;
    setBusyId(r._id);
    try {
      await registrationApi.reject(r._id, reason);
      setNotice({ tone: "info", text: `Rejected: ${r.details?.studentName}. No student was created.` });
    } catch (e) {
      setNotice({ tone: "error", text: errorText(e) });
    } finally {
      setBusyId(null);
      load();
    }
  };

  const waiting = (counts.submitted || 0) + (counts.error || 0) + (counts.processing || 0);
  const tabCount = { submitted: waiting, paid: counts.paid || 0, rejected: counts.rejected || 0 };

  return (
    <div className="ld-page">
      <div className="ld-head">
        <div>
          <h1 className="ld-title">Registrations</h1>
          <p className="ld-subtitle">From the website's "Register now", paid by UPI / bank transfer. Check the payment in the bank or UPI app, then approve to create the student.</p>
        </div>
        <div className="ld-head-actions">
          <Link className="ld-btn" to="/leads">Dashboard</Link>
          <Link className="ld-btn" to="/leads/list">All leads</Link>
        </div>
      </div>

      <div className="ld-tabs" style={{ marginBottom: 14 }}>
        {TABS.map(([k, label]) => (
          <button key={k} type="button" className={`ld-tab${tab === k ? " is-active" : ""}`} onClick={() => setTab(k)}>
            {label}{tabCount[k] != null ? ` (${tabCount[k]})` : ""}
          </button>
        ))}
      </div>

      {notice && (
        <div className={`ld-alert ld-alert-${notice.tone}`} style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <span style={{ flex: 1 }}>{notice.text}</span>
          <button type="button" className="ld-btn ld-btn-sm" onClick={() => setNotice(null)}>Dismiss</button>
        </div>
      )}

      <div className="ld-card">
        <div className="ld-table-wrap">
          <table className="ld-table">
            <thead><tr><th>Submitted</th><th>Student</th><th>Course · batch</th><th>Payment</th><th>Screenshot</th><th>Status</th><th aria-label="Actions" /></tr></thead>
            <tbody>
              {rows.map((r) => {
                const d = r.details || {};
                const [label, s] = STATE[r.status] || [r.status, "skipped"];
                const canDecide = r.paymentMode === "upi" && ["submitted", "error"].includes(r.status);
                return (
                  <tr key={r._id} style={{ cursor: "default" }}>
                    <td className="ld-sub" style={{ whiteSpace: "nowrap" }}>{fmtDateTime(r.createdAt)}</td>
                    <td>
                      <div className="ld-name">{d.studentName}</div>
                      <div className="ld-sub">{d.contactNumber} · {d.emailId}</div>
                      <details><summary className="ld-sub" style={{ cursor: "pointer" }}>All details</summary><Detail d={d} /></details>
                    </td>
                    <td><div>{r.courseTitle}</div><div className="ld-sub">{r.batchTitle}</div></td>
                    <td>
                      <div className="ld-name">{rupees(r.amount)}</div>
                      <div className="ld-sub">{r.paymentMode === "upi" ? "UPI / bank transfer" : "Razorpay"}{r.paymentRef ? ` · ref ${r.paymentRef}` : ""}</div>
                    </td>
                    <td>
                      {r.screenshot?.url ? (
                        <a href={r.screenshot.url} target="_blank" rel="noreferrer" title="Open the screenshot full size">
                          {isPdf(r.screenshot) ? <span className="ld-chip">📄 PDF</span> : <img src={r.screenshot.url} alt="Payment screenshot" className="ld-reg-shot" />}
                        </a>
                      ) : <span className="ld-sub">—</span>}
                    </td>
                    <td>
                      <span className="ld-msg-status" data-s={s}>{label}</span>
                      {r.status === "error" && <div className="ld-field-error" style={{ maxWidth: 220 }}>{r.error}</div>}
                      {r.status === "rejected" && r.rejectReason && <div className="ld-sub">{r.rejectReason}</div>}
                      {r.reviewedBy && <div className="ld-sub">by {r.reviewedBy}{r.reviewedAt ? `, ${fmtDateTime(r.reviewedAt)}` : ""}</div>}
                    </td>
                    <td style={{ whiteSpace: "nowrap" }}>
                      {canDecide && (
                        <>
                          <button type="button" className="ld-btn ld-btn-sm ld-btn-primary" disabled={!!busyId} onClick={() => approve(r)}>{busyId === r._id ? "…" : r.status === "error" ? "Retry approval" : "Approve"}</button>{" "}
                          <button type="button" className="ld-btn ld-btn-sm ld-btn-danger" disabled={!!busyId} onClick={() => reject(r)}>Reject</button>
                        </>
                      )}
                      {r.studentId && <Link className="ld-btn ld-btn-sm" to={`/students/${r.studentId}`}>Open student</Link>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {!rows.length && <div className="ld-empty">{loading ? "Loading…" : tab === "submitted" ? "No registrations waiting for approval." : "Nothing here yet."}</div>}
        </div>
      </div>
    </div>
  );
};

export default LeadRegistrations;
